import { render } from "preact";
import { ChatWidget } from "./ChatWidget";
import type { Theme } from "./types";
// Vite inlines this as a CSS string when imported with ?inline.
import css from "./styles.css?inline";

// Idempotent mount. Loading the script twice (e.g. in a SPA route
// transition) must not produce two widgets. We tag the host element
// so the second load short-circuits.
const HOST_TAG = "nejix-chat-host";
const MOUNTED_FLAG = "__nejixChatMounted";

interface WindowWithFlag extends Window {
  [MOUNTED_FLAG]?: boolean;
}

function readConfig(): {
  siteKey: string;
  agentUrl: string;
  theme: Theme;
} | null {
  const script =
    (document.currentScript as HTMLScriptElement | null) ||
    (document.querySelector('script[data-site-key]') as HTMLScriptElement | null);
  if (!script) {
    console.warn("[nejix-chat] could not locate own <script> tag");
    return null;
  }
  const siteKey = script.dataset.siteKey;
  const agentUrl = script.dataset.agentUrl;
  if (!siteKey) {
    console.warn("[nejix-chat] data-site-key is required");
    return null;
  }
  if (!agentUrl) {
    console.warn("[nejix-chat] data-agent-url is required");
    return null;
  }

  // Optional install-time theme. Parsing here means the launcher
  // button paints branded on the very first render — no waiting for
  // the bootstrap response to arrive. If the attribute is missing or
  // invalid, the widget falls back to the server-provided theme on
  // bootstrap (backward compatible with older embed snippets).
  let theme: Theme = {};
  const themeAttr = script.dataset.theme;
  if (themeAttr) {
    try {
      const parsed: unknown = JSON.parse(themeAttr);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        theme = parsed as Theme;
      } else {
        console.warn("[nejix-chat] data-theme must be a JSON object; ignoring");
      }
    } catch {
      console.warn("[nejix-chat] data-theme could not be parsed as JSON; ignoring");
    }
  }

  return { siteKey, agentUrl, theme };
}

function mount(): void {
  const w = window as WindowWithFlag;
  if (w[MOUNTED_FLAG]) return;

  const config = readConfig();
  if (!config) return;

  // Host element lives in the customer's DOM. Shadow root isolates
  // our styles from theirs in both directions. We do NOT inject any
  // CSS into the light DOM.
  const host = document.createElement(HOST_TAG);
  host.style.cssText = "all: initial;"; // belt-and-suspenders style firewall
  const shadow = host.attachShadow({ mode: "open" });
  document.body.appendChild(host);

  const style = document.createElement("style");
  style.textContent = css;
  shadow.appendChild(style);

  const mountPoint = document.createElement("div");
  mountPoint.className = "nx-root";
  shadow.appendChild(mountPoint);

  render(
    <ChatWidget
      siteKey={config.siteKey}
      agentUrl={config.agentUrl}
      initialTheme={config.theme}
    />,
    mountPoint,
  );

  w[MOUNTED_FLAG] = true;
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mount, { once: true });
} else {
  mount();
}
